import { useRef } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { IconDragComponent } from '@hivebrite/design-system/Icon';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice, useMeasure } from '@hivebrite/react-hooks';

import Drop from 'hb-builder/components/common/Drop';
import EmptyState from 'hb-builder/components/common/state/EmptyState';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import {
  useBuilderContext,
  useComponentContext,
  useLayoutContext,
  usePanelContext,
  useRendererContext,
  useSectionContext,
} from 'hb-builder/hooks';
import { getAlignItems } from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ColumnItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/columnItemAttributes';
import mergeRefs from 'hb-react/shared/utils/mergeRefs';

const interpolateWidth = ({ width = '100%' }: { width: string | number }) =>
  typeof width === 'string' ? width : `${width}%`;

const isColumnEmpty = (column: DocumentItem) => column?.children?.length === 0;

const componentType = 'column';

const Column = () => <div />; // never used

const RenderBuilder = ({ children }) => {
  const { resetPanelTab } = usePanelContext();
  const { isResizing, resetSelectedItem } = useBuilderContext();
  const { item, nbParentChildren } = useComponentContext();
  const { isMobile } = useLayoutContext();
  const { nbFinalChildren, sectionHeight } = useSectionContext();
  const [ref, { height: wrapperHeight, width: wrapperWidth }] = useMeasure();

  const hasSmallHeight = wrapperHeight < 75;
  const isNarrow = wrapperWidth < 200 || hasSmallHeight;
  const svgWrapperHeight = wrapperHeight < 70 ? '100%' : undefined;

  const colRef = useRef<HTMLDivElement | null>(null);

  const attributes = item?.attributes;

  const hasChildren = !isColumnEmpty(item);

  const initialWidth = attributes?.width
    ? parseFloat(attributes.width)
    : 100 / nbParentChildren;

  const handleColumnClick = () => {
    if (isColumnEmpty(item)) {
      resetPanelTab();
      resetSelectedItem();
    }
  };

  return (
    <StyledCol
      // The parent section uses this classname to override the column width on mobile
      className="builder-column"
      $isResizing={isResizing}
      $isMobile={isMobile}
      onClick={handleColumnClick}
      ref={colRef}
      width={interpolateWidth({ width: initialWidth })}
    >
      <Drop
        // Force re-mount when we have/haven't children
        key={hasChildren.toString()}
        accept={['single']}
        noPlaceholder
      >
        {({ isOver, ref: dropRef }) => (
          <StyledWrapper>
            {hasChildren ? (
              <DropStyled ref={dropRef}>
                {/* @ts-expect-error WrapperBuilder is not typed */}
                <WrapperBuilder
                  attributes={attributes}
                  dataTestId="builder-column-with-children-wrapper"
                  style={{
                    flexDirection: 'column',
                    justifyContent: getAlignItems(attributes?.verticalAlign),
                    height: '100%',
                  }}
                >
                  {children}
                </WrapperBuilder>
              </DropStyled>
            ) : (
              <div style={{ position: 'relative', height: '100%' }}>
                {/* @ts-expect-error WrapperBuilder is not typed */}
                <WrapperBuilder
                  attributes={attributes}
                  className="builder-column-empty"
                  dataTestId="builder-column-without-children-wrapper"
                  isEmpty
                  style={{
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  <EmptyState
                    dataTestId="builder-column-wrapper"
                    height={nbFinalChildren === 0 ? 200 : '100%'}
                    isOver={isOver}
                    nbFinalChildren={nbFinalChildren}
                    p={isNarrow ? 0 : undefined}
                    ref={mergeRefs([ref, dropRef])}
                    sectionHeight={sectionHeight}
                  >
                    <Box
                      alignItems="center"
                      flexDirection="column"
                      height={svgWrapperHeight}
                      textAlign="center"
                      width="75%"
                    >
                      <IconDragComponent size={50} />
                      {!isNarrow &&
                        __('Drop a component from the floating panel.')}
                    </Box>
                  </EmptyState>
                </WrapperBuilder>
              </div>
            )}
          </StyledWrapper>
        )}
      </Drop>
    </StyledCol>
  );
};

const Render = ({
  children,
  item = {
    id: '',
    type: '',
  },
}: RenderProps<DocumentItem<ColumnItemAttributes>>) => {
  const { isMobilePreview, scaleRatio } = useRendererContext();
  const { attributes: sectionAttributes, sectionHeight } = useSectionContext();
  const isMobileDevice = useIsMobileDevice();
  const breakpoint = useBreakpoint();

  const { attributes, children: childrenItem } = item;

  // TODO: very complex logic. Should be removed when section min-height is deprecated.
  const isMobile =
    isMobilePreview || isMobileDevice || ['s', 'm'].includes(breakpoint);
  const areColumnSideBySide = sectionAttributes?.mobileDisplay === 'group';
  const shouldApplySectionHeight =
    (!isMobile || (isMobile && areColumnSideBySide)) &&
    scaleRatio === 1 &&
    sectionHeight;
  const hasChildren = childrenItem?.length && childrenItem?.length > 0;
  const columnHeight =
    shouldApplySectionHeight && !hasChildren ? `${sectionHeight}px` : 'auto';

  return (
    // @ts-expect-error Wrapper is not typed
    <Wrapper
      attributes={attributes}
      // The parent section uses this classname to override the column width on mobile
      className="builder-column"
      componentType={componentType}
      isMobilePreview={isMobilePreview}
      style={{
        alignSelf: 'stretch',
        justifyContent: getAlignItems(attributes?.verticalAlign),
        flexDirection: 'column',
        height: columnHeight,
        width: attributes?.width
          ? interpolateWidth({
              width: attributes.width,
            })
          : null,
      }}
    >
      {children}
    </Wrapper>
  );
};

Column.componentName = 'Column';
Column.componentLabel = __('Column');
Column.componentType = componentType;

Column.canBeDoubleClicked = true;
Column.canBeDragged = false;
Column.canBeListed = false;
Column.canBeSelected = false;

Column.RenderBuilder = RenderBuilder;
Column.Render = Render;

Column.item = ({
  disableSelection = false,
  verticalAlign = 'top',
  height = '100%',
  width = '100%',
  children = [],
} = {}) => ({
  attributes: { disableSelection, verticalAlign, height, width },
  type: Column.componentName,
  children,
});

export default Column;

const DropStyled = styled(Box).attrs(() => ({
  height: '100%',
  width: '100%',
}))`
  > div {
    width: 100% !important;
  }
`;

interface StyledColProps {
  $isMobile: boolean;
  $isResizing: boolean;
}

const StyledCol = styled(Box)<StyledColProps>`
  ${({ $isMobile, $isResizing }) => css`
    ${$isMobile && 'flex: 1;'}
    height: auto;
    align-self: stretch;
    opacity: ${$isResizing ? 0.3 : 1};
  `}
`;

const StyledWrapper = styled(Box).attrs(() => ({
  display: 'block',
  height: '100%',
  width: '100%',
}))``;
